import React, { useEffect, useMemo, useState } from "react";
import {
    Drawer,
    Box,
    Typography,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Collapse,
    Divider,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import { blue, purple } from "@/theme/colors";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Link from "next/link";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import { useRouter } from "next/router";

export type SidenavItem = {
    id: string;
    label: string;
    href?: string;
    items?: SidenavItem[];
};

export type SidenavSection = {
    title?: string;
    items: SidenavItem[];
    // Optional custom node for header (e.g., a colored Chip). If provided, used instead of title text.
    titleNode?: React.ReactNode;
};

export interface SidebarState {
    isOpen: boolean;
    isPermanent: boolean;
    sidebarWidth: number;
}

export interface SidenavProps {
    title?: string;
    sections: Array<SidenavSection> | Array<SidenavItem>;
    selectedId?: string;
    onSelect?: (id: string) => void;
    width?: number; // px
    permanentBreakpoint?: "md" | "lg" | "xl"; // when to be permanently open
    toggleEventName?: string; // listen for global event to toggle
    onSidebarStateChange?: (state: SidebarState) => void;
    linkColor?: string; // color for link text
    paletteVariant?: "regulations" | "articles"; // controls header/active colors
    // titles of top-level section groups that should be collapsed by default
    defaultCollapsedSectionTitles?: string[];
    // Optional back link rendered at the very top (above sections)
    backLink?: { href: string; label: string };
}

export default function Sidenav({
    title = "",
    sections,
    selectedId,
    onSelect,
    width = 320,
    permanentBreakpoint = "lg",
    toggleEventName = "sidebarToggle",
    onSidebarStateChange,
    linkColor,
    defaultCollapsedSectionTitles,
    backLink,
    paletteVariant = "regulations",
    ...rest
}: SidenavProps & Record<string, unknown>) {
    const theme = useTheme();
    const router = useRouter();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const isPermanent = useMediaQuery(
        theme.breakpoints.up(permanentBreakpoint),
    );

    // Local open state (desktop persistent + mobile temporary)
    const [mobileOpen, setMobileOpen] = useState(false);
    // Default collapsed when not permanent; will auto-open when permanent
    const [desktopOpen, setDesktopOpen] = useState(false);
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});

    // Styled components for better styling control
    const primaryColor =
        paletteVariant === "articles"
            ? purple.primary
            : theme.palette.primary.main;
    const primaryContrast = theme.palette.primary.contrastText;

    const StyledList = styled(List)(({ theme }) => ({
        padding: 0,
        "& .MuiListItemButton-root": {
            paddingLeft: theme.spacing(2),
            paddingRight: theme.spacing(2),
            "&:hover": {
                backgroundColor: theme.palette.action.hover,
            },
            "&.active": {
                backgroundColor: primaryColor,
                color: primaryContrast,
                "& .MuiListItemText-primary": {
                    fontWeight: 600,
                },
            },
        },
    }));

    // Normalize sections (allow passing flat items list or grouped sections)
    const normalizedSections: SidenavSection[] = useMemo(() => {
        if (!Array.isArray(sections)) return [];
        const arr = sections as Array<SidenavSection | SidenavItem>;
        if (arr.length && (arr[0] as SidenavSection).items) {
            return arr as SidenavSection[];
        }
        return [{ items: arr as SidenavItem[] }];
    }, [sections]);

    // Expand groups initially; force all open for now (default-collapsed disabled)
    useEffect(() => {
        const map: Record<string, boolean> = {};
        normalizedSections.forEach((sec, sIdx) => {
            // const shouldCollapse =
            //     !!sec.title &&
            //     Array.isArray(defaultCollapsedSectionTitles) &&
            //     defaultCollapsedSectionTitles.includes(sec.title);
            // map[`section-${sIdx}`] = !shouldCollapse;
            map[`section-${sIdx}`] = true; // keep open by default
            sec.items.forEach((item) => {
                if (item.items?.length) map[item.id] = true;
            });
        });
        setExpanded(map);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Toggle handler via global event
    const handleToggle = React.useCallback(() => {
        if (isPermanent) return; // ignore when permanent
        if (isMobile) setMobileOpen((v) => !v);
        else setDesktopOpen((v) => !v);
    }, [isPermanent, isMobile]);

    useEffect(() => {
        window.addEventListener(toggleEventName, handleToggle as EventListener);
        return () =>
            window.removeEventListener(
                toggleEventName,
                handleToggle as EventListener,
            );
    }, [toggleEventName, handleToggle]);

    // Keep sidebar open when permanent; ensure mobile is closed when becoming permanent
    useEffect(() => {
        if (isPermanent) {
            setDesktopOpen(true);
            setMobileOpen(false);
        } else {
            // Leaving permanent (resize below lg): collapse back to the
            // pull-tab. A stale desktopOpen=true here left the drawer
            // covering the unshifted navbar between md and lg.
            setDesktopOpen(false);
        }
    }, [isPermanent]);

    // Notify parent of state
    useEffect(() => {
        onSidebarStateChange?.({
            isOpen: isPermanent ? true : isMobile ? mobileOpen : desktopOpen,
            isPermanent,
            sidebarWidth: width,
        });
    }, [
        onSidebarStateChange,
        isPermanent,
        isMobile,
        mobileOpen,
        desktopOpen,
        width,
    ]);

    const handleClickGroup = (id: string) => {
        setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const isActiveHref = (href?: string) => {
        if (!href) return false;
        // Basic check against current path
        // Support dynamic routes by startsWith
        try {
            const current = router.asPath.split("?")[0];
            return current === href || current.startsWith(href + "/");
        } catch {
            return false;
        }
    };

    const renderItem = (item: SidenavItem, depth = 0) => {
        const hasChildren = !!item.items?.length;
        const active = selectedId === item.id || isActiveHref(item.href);

        if (hasChildren) {
            return (
                <Box key={item.id}>
                    <ListItemButton
                        onClick={() => handleClickGroup(item.id)}
                        sx={{ pl: 2 + depth * 2 }}
                        className={active ? "active" : undefined}
                    >
                        <ListItemText primary={item.label} />
                        {expanded[item.id] ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                    <Collapse
                        in={!!expanded[item.id]}
                        timeout="auto"
                        unmountOnExit
                    >
                        <List component="div" disablePadding>
                            {item.items!.map((child) =>
                                renderItem(child, depth + 1),
                            )}
                        </List>
                    </Collapse>
                </Box>
            );
        }

        const button = (
            <ListItem
                key={item.id}
                disablePadding
                sx={{
                    pl: depth > 0 ? 2 : 0, // Add indentation for nested items
                }}
            >
                <ListItemButton
                    onClick={(e) => {
                        if (item.href) {
                            e.preventDefault();
                            router.push(item.href);
                            onSelect?.(item.id);
                        } else {
                            onSelect?.(item.id);
                        }
                        if (isMobile) setMobileOpen(false);
                    }}
                    sx={{ pl: 2 + depth * 2 }}
                    className={active ? "active" : undefined}
                    component={item.href ? "a" : "div"}
                >
                    <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                            sx: {
                                fontSize: depth > 0 ? "0.875rem" : "1rem",
                                lineHeight: 1.2,
                                color: active
                                    ? undefined
                                    : linkColor || theme.palette.primary.main,
                            },
                        }}
                    />
                </ListItemButton>
            </ListItem>
        );

        return item.href ? button : button;
    };

    const content = (
        <Box
            {...rest}
            sx={{ height: "100%", display: "flex", flexDirection: "column" }}
        >
            {backLink && (
                <Box sx={{ px: 1, pt: 1 }}>
                    <ListItem disablePadding>
                        <ListItemButton
                            component={Link}
                            href={backLink.href}
                            onClick={(e) => {
                                // Let Next.js handle, but also close mobile drawer immediately
                                if (isMobile) setMobileOpen(false);
                            }}
                            sx={{
                                borderRadius: 1,
                                "& .MuiListItemText-primary": {
                                    color:
                                        linkColor || theme.palette.primary.main,
                                },
                            }}
                        >
                            <ArrowBackIcon
                                fontSize="small"
                                sx={{
                                    mr: 1,
                                    color: linkColor || primaryColor,
                                }}
                            />
                            <ListItemText primary={backLink.label} />
                        </ListItemButton>
                    </ListItem>
                </Box>
            )}
            {title && (
                <Box
                    sx={{
                        p: 2,
                        bgcolor: primaryColor,
                        color: primaryContrast,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                    }}
                >
                    <Typography variant="h6">{title}</Typography>
                    {!isPermanent && (
                        <Box
                            role="button"
                            aria-label="Close navigation"
                            onClick={() =>
                                isMobile
                                    ? setMobileOpen(false)
                                    : setDesktopOpen(false)
                            }
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                color: primaryContrast,
                            }}
                        >
                            <CloseIcon />
                        </Box>
                    )}
                </Box>
            )}
            <StyledList sx={{ py: 0 }}>
                {normalizedSections.map((sec, idx) => (
                    <Box key={idx}>
                        {(sec.title || sec.titleNode) && (
                            <ListItemButton
                                onClick={() =>
                                    setExpanded((prev) => ({
                                        ...prev,
                                        [`section-${idx}`]:
                                            !prev[`section-${idx}`],
                                    }))
                                }
                                sx={{ px: 2 }}
                            >
                                {sec.titleNode ? (
                                    <Box
                                        sx={{
                                            flex: 1,
                                            display: "flex",
                                            alignItems: "center",
                                        }}
                                    >
                                        {sec.titleNode}
                                    </Box>
                                ) : (
                                    <ListItemText
                                        primary={sec.title}
                                        primaryTypographyProps={{
                                            variant: "overline",
                                            sx: { color: "text.secondary" },
                                        }}
                                    />
                                )}
                                {expanded[`section-${idx}`] ? (
                                    <ExpandLess fontSize="small" />
                                ) : (
                                    <ExpandMore fontSize="small" />
                                )}
                            </ListItemButton>
                        )}
                        <Collapse
                            in={expanded[`section-${idx}`]}
                            timeout="auto"
                            unmountOnExit
                        >
                            {sec.items.map((item) => renderItem(item))}
                        </Collapse>
                        {idx < normalizedSections.length - 1 && (
                            <Divider sx={{ my: 1 }} />
                        )}
                    </Box>
                ))}
            </StyledList>
        </Box>
    );

    // lg+ (navbar shifted beside the nav): persistent, always open.
    // Below lg the navbar does NOT shift, so an open persistent drawer
    // would cover it — every non-permanent width uses the temporary
    // overlay (backdrop, tap-away close) opened from the pull tab.
    const variant = isPermanent ? "persistent" : "temporary";
    const open = isPermanent ? true : isMobile ? mobileOpen : desktopOpen;

    return (
        <>
            {/* Pull Tab: visible when the sidebar is closed and not permanent */}
            {!open && !isPermanent && (
                <Box
                    role="button"
                    aria-label="Open navigation"
                    onClick={() =>
                        isMobile ? setMobileOpen(true) : setDesktopOpen(true)
                    }
                    sx={{
                        position: "fixed",
                        left: 0,
                        top: "50%",
                        transform: "translateY(-50%)",
                        width: 36,
                        height: 72,
                        bgcolor: alpha(primaryColor, 0.5),
                        color: primaryContrast,
                        borderRadius: "0 8px 8px 0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        boxShadow: 1,
                        zIndex: (theme) => theme.zIndex.drawer + 1,
                    }}
                >
                    <ChevronRightIcon />
                </Box>
            )}
            <Drawer
                variant={variant}
                anchor="left"
                open={open}
                onClose={() => {
                    setMobileOpen(false);
                    setDesktopOpen(false);
                }}
                PaperProps={{ sx: { width } }}
            >
                {content}
            </Drawer>
        </>
    );
}
